import {
  AuthSession,
  CurrentUser,
  WalletModel,
} from '@fantaz/shared/data-access/models';
import {
  createAction,
  createActionGroup,
  emptyProps,
  props,
} from '@ngrx/store';
import { AuthParamsEntity } from './auth.models';

type IAuthDevice = any;

/**
 * Auth actions
 */
export const AuthAuthActions = createActionGroup({
  source: 'Auth',
  events: {
    Initialize: () => emptyProps(),
    /**
     * Action to sign in with the specified payload.
     * @param payload - The payload for the sign in action.
     * @returns An object representing the sign in action.
     */

    'Post Sign In': () => emptyProps(),

    /**
     * Action to logout.
     * @returns An object representing the logout action.
     */
    Logout: () => emptyProps(),
    LogoutSuccess: () => emptyProps(),

    /**
     * Action to rehydrate the session.
     * @param payload - The payload for the rehydrate session action.
     * @returns An object representing the rehydrate session action.
     */
    'Rehydrate Session': (payload: { session: AuthSession }) => ({
      ...payload,
    }),

    'Continue To GetStarted': () => emptyProps(),
  },
});

/**
 * Current user actions
 */
/**
 * Actions related to the authentication session.
 */
export const AuthSessionActions = createActionGroup({
  source: 'Auth/Sessions',
  events: {
    /**
     * Action dispatched when loading the authentication session is in progress.
     */
    'Load Auth Session In Progress': () => emptyProps(),

    /**
     * Action dispatched when loading the authentication session is successful.
     * @param payload - The payload containing the session object.
     */
    'Load Auth Session Success': (payload: { session: AuthSession }) => ({
      ...payload,
    }),

    /**
     * Action dispatched when loading the authentication session fails.
     * @param error - The error object.
     */
    'Load Auth Session Failure': (error: any) => ({ error }),
  },
});

/**
 * Current user actions
 */
export const AuthCurrentUserActions = createActionGroup({
  source: 'Auth/CurrentUser',
  events: {
    /**
     * Action to load the current user.
     * @returns An object representing the load user action.
     */
    'Load Current User In Progress': (options?: { silent?: boolean }) => ({
      options,
    }),

    /**
     * Action to load the current user.
     * @param currentUser - The current user.
     * @returns An object representing the load user success action.
     */
    'Load Current User Success': (currentUser: CurrentUser) => ({
      currentUser,
    }),

    /**
     * Action to load the current user.
     * @param error - The error that occurred while loading the current user.
     * @returns An object representing the load user failure action.
     */
    'Load Current User Failure': (error: any) => ({ error }),
  },
});

/**
 * Local storage actions
 */
export const AuthLocalStorageActions = createActionGroup({
  source: 'Auth/LocalStorage',
  events: {
    /**
     * Action to save user session in local storage.
     * @param session - The user session to be saved.
     */
    'Save User Session': (session: AuthSession) => ({ session }),
  },
});

export const signIn = createAction(
  'Auth SignIn Init',
  props<{ parameters: AuthParamsEntity }>(),
);

export const signInSuccess = createAction(
  'Auth SignIn Success',
  props<{ auth: AuthSession }>(),
);

export const signInFailure = createAction(
  'Auth/API SignIn Failure',
  props<{ error: any }>(),
);

export const rehydrateSession = createAction(
  'Auth/API Rehydrate Session',
  props<{ auth: AuthSession }>(),
);

export const relogin = createAction(
  'Auth/API Refresh Session',
  props<{ username: string; password: string }>(),
);

export const reloginSuccess = createAction(
  'Auth/API Refresh Session Success',
  props<{ auth: AuthSession }>(),
);

export const logout = createAction(
  'Auth/API Logout',
  props<{ redirectTo?: string }>(),
);
export const loginRedirect = createAction(
  'Auth/API Login Redirect',
  props<{ redirectTo?: string }>(),
);

export const loadUserDevices = createAction('Auth/API load user devices');
export const loadUserDevicesSuccess = createAction(
  'Auth/API load user devices success',
  props<{ devices: IAuthDevice[] }>(),
);
export const loadUserDevicesFailure = createAction(
  'Auth/API load user devices failure',
  props<{ error: any }>(),
);

/**
 * User session
 */

export const loadUser = createAction('User Page Load User');

export const loadUserSuccess = createAction(
  'User/API Load User Success',
  props<{ currentUser: CurrentUser | null | undefined }>(),
);

export const updateUserLocally = createAction(
  'User/Store Update User Locally',
  props<{ user: any }>(), // HACK: add dynamic User partial for all props
);

export const loadUserFailure = createAction(
  'User/API Load User Failure',
  props<{ error: any }>(),
);

export const loadWallet = createAction('[User/API] Load Wallet');

export const loadWalletSuccess = createAction(
  '[User/API] Load Wallet Success',
  props<{ wallet: WalletModel | null }>(),
);

export const loadWalletFailure = createAction(
  '[User/API] Load Wallet Failure',
  props<{ error: any }>(),
);

export const init = createAction('[Auth/API] init');
export const loaded = createAction(
  '[Auth/API] Loaded',
  props<{ loaded?: boolean; currentUser?: CurrentUser }>(),
);
export const loadAuthSession = createAction('[Auth/API] Load Auth Session');

export const AuthActions = {
  init,
  loadAuthSession,
  loaded,
  loadWallet,
};
